<script setup>
import {
  BIconChevronLeft,
  BIconChevronDoubleLeft,
  BIconChevronRight,
  BIconChevronDoubleRight,
} from "bootstrap-icons-vue";

const emit = defineEmits(["update:model-value"]);

const props = defineProps({
  modelValue: {
    type: Number,
    default: 1,
  },
  totalRows: {
    type: Number,
    default: 0,
  },
  perPage: {
    type: Number,
    default: 1,
  },
});

const totalPages = computed(() => Math.ceil(props.totalRows / props.perPage));

const cannotGoBack = computed(() => {
  return props.modelValue === 1;
});
const cannotGoNext = computed(() => {
  return props.modelValue === totalPages.value;
});

const updateModelValue = (value) => {
  emit("update:model-value", Math.max(1, Math.min(value, totalPages.value)));
};

const displayedPages = computed(() => {
  const currentPage = props.modelValue;
  const pages = [];

  if (totalPages.value <= 3) {
    for (let i = 1; i <= totalPages.value; i++) {
      pages.push(i);
    }
  } else {
    if (currentPage === 1) {
      pages.push(1, 2, 3);
    } else if (currentPage === totalPages.value) {
      pages.push(totalPages.value - 2, totalPages.value - 1, totalPages.value);
    } else {
      pages.push(currentPage - 1, currentPage, currentPage + 1);
    }
  }

  return pages;
});
</script>

<template>
  <div class="pagination-container d-flex column-gap-2 align-items-center">
    <span class="page-item d-flex justify-content-center align-items-center">
      <BIconChevronDoubleLeft
        class="move-btn cursor-pointer"
        :class="{ disabled: cannotGoBack }"
        @click="!cannotGoBack && updateModelValue(1)"
      />
    </span>

    <span class="page-item d-flex justify-content-center align-items-center">
      <BIconChevronLeft
        class="move-btn cursor-pointer"
        :class="{ disabled: cannotGoBack }"
        @click="!cannotGoBack && updateModelValue(modelValue - 1)"
      />
    </span>

    <button
      v-for="page in displayedPages"
      :key="page"
      class="page-item page-item-button d-flex justify-content-center align-items-center"
      :class="{ active: modelValue === page }"
      @click="updateModelValue(page)"
    >
      {{ page }}
    </button>

    <span class="page-item d-flex justify-content-center align-items-center">
      <BIconChevronRight
        class="move-btn cursor-pointer"
        :class="{ disabled: cannotGoNext }"
        @click="!cannotGoNext && updateModelValue(modelValue + 1)"
      />
    </span>

    <span class="page-item d-flex justify-content-center align-items-center">
      <BIconChevronDoubleRight
        class="move-btn cursor-pointer"
        :class="{ disabled: cannotGoNext }"
        @click="!cannotGoNext && updateModelValue(totalPages)"
      />
    </span>
  </div>
</template>

<style scoped lang="scss">
.pagination-container {
  margin-bottom: 0;

  .page-item {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid $neutral-150;
    color: $neutral-350;
    background-color: white;
    cursor: pointer;

    &.active {
      background-color: $neutral-100;
      color: $neutral-300;
    }
  }

  :deep(.move-btn) {
    color: $neutral-350;

    &.disabled {
      cursor: not-allowed;
    }
  }
}
</style>
